import React from 'react';
import { Reveal } from '../Reveal';
import { kordisCoreFeatures, kordisFrictions, kordisHybridModel, kordisInsight, kordisPipeline, kordisTeamAnswer, kordisTeamQuestions } from '../../data/kordisDiscovery';
type BoardLabelProps = {
  children: React.ReactNode;
};
function BoardLabel({
  children
}: BoardLabelProps) {
  return <h3 className="text-sm font-semibold tracking-tight text-[#1E1E1E]" data-id="element-404" data-mp="2212i7zy8nawc">{children}</h3>;
}

/** Curated slice of the Kordis discovery board: questions → what's broken → the insight → what to build. */
export function DiscoveryBoard() {
  return <div className="space-y-4" data-id="element-405" data-mp="1edb7v1w9d7xp">
      {/* 1 · Questions + answer */}
      <Reveal data-id="element-406" data-mp="1eaygjy4m1l7z">
        <div className="rounded-2xl border border-[#CDF4D3] bg-[#EBFFEE] p-5 md:p-8" data-id="element-407" data-mp="1jc5njuyxigdl">
          <BoardLabel data-id="element-408" data-mp="1j9rtnpfzja7m">Questions I brought to the team</BoardLabel>
          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-409" data-mp="14g6g8mjn8k9i">
            {kordisTeamQuestions.map(q => <p key={q} className="min-h-[120px] rounded-sm bg-[#D9CCF7] p-4 text-[13px] leading-snug text-[#1E1E1E] shadow-[0_3px_2px_rgba(0,0,0,0.04)]" data-id="element-410" data-mp="19dghr0kb4mdp">
                {q}
              </p>)}
          </div>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4" data-id="element-411" data-mp="k5nkx2hmurzb">
            <span className="text-xs font-medium text-[#4C6B52]" data-id="element-412" data-mp="g38y67auf82r">The answer that framed the product</span>
            <p className="w-fit -rotate-1 rounded-sm bg-[#B3EFBD] px-4 py-3 text-[13px] text-[#1E1E1E] shadow-[0_3px_2px_rgba(0,0,0,0.04)]" data-id="element-413" data-mp="3oissl9n92wje">
              &ldquo;{kordisTeamAnswer}&rdquo;
            </p>
          </div>
        </div>
      </Reveal>

      {/* 2 · What's broken + the insight */}
      <div className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]" data-id="element-414" data-mp="3skxhjv2r74zp">
        <Reveal className="h-full" data-id="element-415" data-mp="34vcwn6n1hxns">
          <div className="h-full rounded-2xl border border-line bg-raised p-5 md:p-7" data-id="element-416" data-mp="34ua7p49a25ai">
            <h3 className="text-sm font-semibold text-fg" data-id="element-417" data-mp="30usxjxiwojz0">What finance leaders said wasn't working</h3>
            <p className="mt-1 text-xs text-fg-faint" data-id="element-418" data-mp="13sc3jyepg21d">From r/FPandA, r/SaaS and fractional CFO communities</p>
            <ul className="mt-5 divide-y divide-line" data-id="element-419" data-mp="18r5fvni99mzy">
              {kordisFrictions.map(f => <li key={f.title} className="py-3 first:pt-0 last:pb-0" data-id="element-420" data-mp="1dkhrmx1u5zba">
                  <p className="text-sm font-semibold text-fg" data-id="element-421" data-mp="1iijabgvcnwm8">{f.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-fg-muted" data-id="element-422" data-mp="kuj270jqc063">{f.body}</p>
                </li>)}
            </ul>
          </div>
        </Reveal>
        <Reveal className="h-full" delay={0.05} data-id="element-423" data-mp="pvoskrpx3bcg">
          <figure className="flex h-full flex-col justify-between rounded-2xl bg-ink p-6 text-white md:p-7" data-id="element-424" data-mp="usfjka39frcn">
            <blockquote className="text-2xl font-semibold leading-tight tracking-tight md:text-[28px]" data-id="element-425" data-mp="zstgox3bpgvw">
              &ldquo;{kordisInsight.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-6" data-id="element-426" data-mp="2b6fgdc6t70vo">
              <p className="text-xs text-white/60" data-id="element-427" data-mp="2b5dtmagfkpy4">{kordisInsight.source}</p>
              <p className="mt-4 border-t border-white/15 pt-4 text-sm leading-relaxed text-white/85" data-id="element-428" data-mp="3vny4bv2mcv7m">
                <span className="font-semibold text-white" data-id="element-429" data-mp="3qr7o97qtdzm7">The hybrid model. </span>
                {kordisHybridModel}
              </p>
            </figcaption>
          </figure>
        </Reveal>
      </div>

      {/* 3 · What to build */}
      <Reveal data-id="element-430" data-mp="3gy8znpvbi92l">
        <div className="rounded-2xl border border-[#CDF4D3] bg-[#EBFFEE] p-5 md:p-8" data-id="element-431" data-mp="3c10fxkuitwz8">
          <BoardLabel data-id="element-432" data-mp="3bynpskem0gb3">Core features we landed on</BoardLabel>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-433" data-mp="37ylyn56i35a2">
            {kordisCoreFeatures.map(feature => <li key={feature} className="flex aspect-square items-start rounded-sm bg-[#B3EFBD] p-3.5 text-[13px] font-medium leading-snug text-[#1E1E1E] shadow-[0_3px_2px_rgba(0,0,0,0.04)]" data-id="element-434" data-mp="33257e2408asr">
                {feature}
              </li>)}
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs text-[#4C6B52]" data-id="element-435" data-mp="3q23tmoled0a1">
            <span className="mr-1 font-medium" data-id="element-436" data-mp="3u35sv5fbdcc0">Under the hood:</span>
            {kordisPipeline.map((step, i) => <React.Fragment key={step} data-id="element-437" data-mp="3g4fxpvc21mqa" data-mpi={i}>
                <span className="rounded-full border border-[#CDF4D3] bg-white px-2.5 py-1 font-mono text-[11px] text-[#1E1E1E]" data-id="element-438" data-mp="3g3enft2v549m">
                  {step}
                </span>
                {i < kordisPipeline.length - 1 && <span aria-hidden data-id="element-439" data-mp="36c0fcwwuiw4v">→</span>}
              </React.Fragment>)}
          </div>
        </div>
      </Reveal>
    </div>;
}